/**
 * The three controls a workspace screen only has on a phone.
 *
 * All three are rendered unconditionally and hidden by CSS above 760px rather
 * than being switched on a `useIsNarrow()` in every caller. Two reasons: a
 * media query cannot disagree with itself the way two mirrored breakpoints can,
 * and a control that is only mounted below a width is a control that pops into
 * existence mid-resize, taking focus and layout with it.
 */
import type { ReactNode } from "react";
import { ArrowLeft, PanelLeft } from "lucide-react";

/** Opens the section rail. Sits first in the list pane's head, where the rail
 *  it stands in for used to be. */
export function RailTrigger({ label, onOpen }: { label: string; onOpen: () => void }) {
  return (
    <button type="button" className="crm-cb crm-work-only-narrow" onClick={onOpen} title={label} aria-label={label}>
      <PanelLeft className="size-4" />
    </button>
  );
}

/** Back out of the open item, into the list. Sits first in the reading pane's
 *  header, because on a phone that header is the top of the screen. */
export function PaneBack({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <button
      type="button"
      className="crm-cb crm-work-only-narrow"
      onClick={onBack}
      title={label}
      aria-label={label}
    >
      <ArrowLeft className="size-4" />
    </button>
  );
}

/**
 * The screen's main action - write a mail, place a call - as a floating button.
 *
 * It is drawn a second time here rather than moved out of the rail, and that is
 * the cheaper of the two mistakes. Composing is why an operator opens the mail
 * desk at all; behind a sheet it costs two taps and a decision about where the
 * button lives. The rail keeps its own wide, labelled version for the screens
 * that have room for a rail.
 */
export function WorkFab({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="crm-work-fab crm-work-only-narrow" onClick={onClick} title={label} aria-label={label}>
      {children}
    </button>
  );
}

/** The blurred sheet of glass under an open rail. A tap on it means "close". */
export function RailScrim({ open, onClose }: { open: boolean; onClose: () => void }) {
  return <div className={`crm-work-scrim${open ? " is-on" : ""}`} onClick={onClose} aria-hidden="true" />;
}
